// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";
import type { LiteralUnion } from "type-fest";

import type { QdsFocusEventDetail } from "../../helpers";
import { pickFocusEventAttributes } from "../../helpers";
import state from "../store";
import { onMobileBreakpoint } from "../utils";

export type Target = "_blank" | "_parent" | "_self" | "_top";
/**
 * @slot navigation - Content to display in the navigation slot of the nav item.
 * @slot dropdown - Content to display in the dropdown slot of the nav item.
 */

const isCounter = (value: unknown): value is number =>
  typeof value === "string"
    ? !Number.isNaN(Number.parseFloat(value))
    : typeof value === "number";

@Component({
  tag: "qds-web-nav-item",
  shadow: { delegatesFocus: true },
  styleUrl: "nav-item.css",
})
export class NavItem implements ComponentInterface {
  /**
   * The nav item's label.
   */
  @Prop() public readonly label?: string;

  /**
   * Displays a counter badge if a number or string representing a number is used.
   */
  @Prop() public readonly badge?: number | string;

  /**
   * The counter badge's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly badgeDescription?: string;

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = "default";

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string;

  /**
   * Defines the text of the tagLabel tag and whether it should be shown or not.
   *
   * If you don't want to show the tagLabel tag, simply don't specify this prop.
   *
   * @prop {string} tagLabel - The text of the tagLabel tag.
   */
  @Prop() public tagLabel: string;

  /**
   * Causes the browser to treat the linked URL as a download. Can be used with
   * or without a `filename` value:
   *
   * - Without a value, the browser will suggest a filename/extension,
   * generated from various sources:
   *
   *   - The
   *   [`Content-Disposition`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Content-Disposition)
   *   HTTP header
   *   - The final segment in the URL
   *   [path](https://developer.mozilla.org/docs/Web/API/URL/pathname)
   *   - The
   *   [media type](https://developer.mozilla.org/docs/Glossary/MIME_type)
   *   (from the
   *   [`Content-Type`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Content-Type)
   *   header, the start of a
   *   [`data:` URL](https://developer.mozilla.org/docs/Web/HTTP/Basics_of_HTTP/Data_URLs),
   *   or
   *   [`Blob.type`](https://developer.mozilla.org/docs/Web/API/Blob/type)
   *   for a
   *   [`blob:` URL](https://developer.mozilla.org/docs/Web/API/URL/createObjectURL))
   * - `filename`: defining a value suggests it as the filename. `/` and `\`
   * characters are converted to underscores (`_`). Filesystems may forbid
   * other characters in filenames, so browsers will adjust the suggested name
   * if necessary.
   *
   * @webnative
   */
  @Prop() public readonly download?: boolean | string;

  /**
   * When set, the nav-item will redirect the user to the specified href.
   */
  @Prop() public readonly href?: string;

  /**
   * Hints at the human language of the linked URL. No built-in functionality.
   * Allowed values are the same as
   * [the global `lang` attribute](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/lang).
   *
   * @webnative
   */
  @Prop() public readonly hreflang?: string;

  /**
   * How much of the
   * [referrer](https://developer.mozilla.org/docs/Web/HTTP/Headers/Referer)
   * to send when following the nav item.
   *
   * - `no-referrer`: The
   * [`Referer`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Referer)
   * header will not be sent.
   * - `no-referrer-when-downgrade`: The
   * [`Referer`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Referer)
   * header will not be sent to
   * [origin](https://developer.mozilla.org/docs/Glossary/Origin)s
   * without [TLS](https://developer.mozilla.org/docs/Glossary/TLS)
   * ([HTTPS](https://developer.mozilla.org/docs/Glossary/HTTPS)).
   * - `origin`: The sent referrer will be limited to the origin of the
   * referring page: its
   * [scheme](https://developer.mozilla.org/docs/Learn/Common_questions/What_is_a_URL),
   * [host](https://developer.mozilla.org/docs/Glossary/Host), and
   * [port](https://developer.mozilla.org/docs/Glossary/Port).
   * - `origin-when-cross-origin`: The referrer sent to other origins will be
   * limited to the scheme, the host, and the port. Navigations on the same
   * origin will still include the path.
   * - `same-origin`: A referrer will be sent for
   * [same origin](https://developer.mozilla.org/docs/Glossary/Same-origin_policy),
   * but cross-origin requests will contain no referrer information.
   * - `strict-origin`: Only send the origin of the document as the referrer
   * when the protocol security level stays the same (HTTPS→HTTPS), but don't
   * send it to a less secure destination (HTTPS→HTTP).
   * - `strict-origin-when-cross-origin` (default): Send a full URL when
   * performing a same-origin request, only send the origin when the protocol
   * security level stays the same (HTTPS→HTTPS), and send no header to a less
   * secure destination (HTTPS→HTTP).
   * - `unsafe-url`: The referrer will include the origin and the path (but not the
   * [fragment](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/hash),
   * [password](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/password),
   * or [username](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/username)).
   * **This value is unsafe** because it leaks origins and paths from
   * TLS-protected resources to insecure origins.
   *
   * @webnative
   */
  @Prop({ attribute: "referrerpolicy" })
  public readonly referrerPolicy?: ReferrerPolicy;

  /**
   * The relationship of the linked URL as space-separated
   * [link types](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel).
   *
   * The following list includes some of the most important existing keywords.
   * Every keyword within a space-separated value should be unique within that
   * value.
   *
   * - [`alternate`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-alternate):
   * Alternate representations of the current document.
   * - [`author`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-author):
   * Author of the current document or article.
   * - [`bookmark`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-bookmark):
   * Permalink for the nearest ancestor section.
   * - [`external`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-external):
   * The referenced document is not part of the same site as the current
   * document. An icon will be rendered next to the text if this value is
   * specified.
   * - [`help`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-help):
   * Link to context-sensitive help.
   * - [`license`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-license):
   * Indicates that the main content of the current document is covered by the
   * copyright license described by the referenced document.
   * - [`me`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel/me):
   * Indicates that the current document represents the person who owns the
   * linked content.
   * - [`next`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-next):
   * Indicates that the current document is a part of a series and that the
   * next document in the series is the referenced document.
   * - [`nofollow`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-nofollow):
   * Indicates that the current document's original author or publisher does
   * not endorse the referenced document.
   * - [`noopener`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel/noopener):
   * Creates a top-level browsing context that is not an auxiliary browsing
   * context if the hyperlink would create either of those, to begin with (i.e.,
   * has an appropriate `target` attribute value).
   * - [`noreferrer`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-noreferrer):
   * No `Referer` header will be included. Additionally, has the same effect as
   * `noopener`.
   * - [`opener`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-opener):
   * Creates an auxiliary browsing context if the hyperlink would otherwise
   * create a top-level browsing context that is not an auxiliary browsing
   * context (i.e., has "`_blank`" as `target` attribute value).
   * - [`prev`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-prev):
   * Indicates that the current document is a part of a series and that the
   * previous document in the series is the referenced document.
   * - [`search`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-search):
   * Gives a link to a resource that can be used to search through the current
   * document and its related pages.
   * - [`tag`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-tag):
   * Gives a tag (identified by the given address) that applies to the current
   * document.
   *
   * @webnative
   */
  @Prop() public readonly rel?: string;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string>;

  @State() public open = false;

  @State() public isCurrentTab: boolean;

  @State() public isMobile: boolean;

  /**
   * Emitted when the navItem is clicked.
   */
  @Event({ eventName: "qdsClick", bubbles: false, cancelable: false })
  public clickEmitter: EventEmitter<void>;

  /**
   * Emitted when the navItem is hovered.
   */
  @Event({ eventName: "qdsHover", bubbles: false, cancelable: false })
  public hoverEmitter: EventEmitter<void>;

  /**
   * Emitted when the corresponding megamenu is closed.
   */

  @Event({ eventName: "qds-nav-item:close" })
  public closeNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the corresponding megamenu is opened.
   */

  @Event({ eventName: "qds-nav-item:open" })
  public openNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the navItem is focused.
   */
  @Event({ eventName: "qdsFocus", bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>;

  /**
   * Emitted when the navItem loses focus.
   */
  @Event({ eventName: "qdsBlur", bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>;

  @Element() private readonly el!: HTMLLIElement;

  private cleanupMobileListener?: () => void;

  @Listen("qds-nav-item:close", { target: "document" })
  public handleCloseNavTabsEvent(event: CustomEvent<boolean>): void {
    if (this.isMobile) {
      state.tab = "";
    }
    if (!this.isCurrentTab || event.detail) {
      this.open = false;
    }
    this.isCurrentTab = false;
    this.openNavTabs.emit(false);
  }

  @Listen("keydown")
  public handleKeyDownEvent(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.closeNavTabs.emit(true);
      this.openNavTabs.emit(!!this.open);
    }
  }

  public handleTabItemClick(): void {
    this.isCurrentTab = true;
    this.closeNavTabs.emit(false);
    this.open = !this.open;
    state.tab = this.label ?? "";
    if (!this.isMobile && this.open) {
      const checkInterval = setInterval(() => {
        const element = this.el.querySelector("qds-web-megamenu-item");
        if (element) {
          state.item =
            this.el
              .querySelector("qds-web-megamenu-item")
              ?.getAttribute("id") ?? "";
          clearInterval(checkInterval);
        }
      }, 50);
    }
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public render() {
    const Tag = this.href === undefined || this.href === "" ? "button" : "a";
    return (
      <Host role="listitem">
        <div
          class={{
            web: !this.isMobile,
            mobile: this.isMobile,
            open: this.open,
          }}
        >
          {this.isMobile ? (
            <Tag
              class="mobile-item"
              onClick={() => {
                this.handleTabItemClick();
                this.clickEmitter.emit();
              }}
              href={Tag === "a" ? this.href : undefined}
              target={Tag === "a" ? this.target : undefined}
              type={Tag === "a" ? undefined : "button"}
              role={Tag === "a" ? undefined : "button"}
              onMouseEnter={() => {
                this.hoverEmitter.emit();
              }}
            >
              <div class="qds-mobile-title">{this.label}</div>
              <qds-icon
                aria-hidden="true"
                class="qds-icon qds-destination"
                library="core"
                name="navigation"
              />
            </Tag>
          ) : (
            <Tag
              class="tabs-item"
              href={Tag === "a" ? this.href : undefined}
              target={Tag === "a" ? this.target : undefined}
              rel={Tag === "a" ? this.rel : undefined}
              download={Tag === "a" ? this.download : undefined}
              type={Tag === "a" ? undefined : "button"}
              role={Tag === "a" ? undefined : "button"}
              aria-expanded={
                Tag === "a" ? undefined : this.open ? "true" : "false"
              }
              aria-haspopup={Tag === "a" ? undefined : "true"}
              hreflang={Tag === "a" ? this.hreflang : undefined}
              referrerPolicy={Tag === "a" ? this.referrerPolicy : undefined}
              onClick={() => {
                if (Tag === "button") {
                  this.handleTabItemClick();
                }
                if (Tag === "a") {
                  this.closeNavTabs.emit(true);
                }
                this.clickEmitter.emit();
                this.openNavTabs.emit(!!this.open);
              }}
              onBlur={this.onBlur}
              onFocus={this.onFocusHandler}
              onMouseEnter={() => {
                this.hoverEmitter.emit();
              }}
            >
              <div class="tab">
                <qds-tag
                  class={{
                    hide: !this.tagLabel,
                  }}
                  text={this.tagLabel}
                  importance="emphasized"
                  size="small"
                  status="info"
                />
                {this.iconName !== undefined && this.iconName !== "" && (
                  <qds-icon
                    aria-hidden="true"
                    class="qds-icon"
                    library={this.iconLibrary}
                    name={this.iconName}
                  />
                )}
                {this.label}

                {isCounter(this.badge) && (
                  <qds-badge-counter
                    description={this.badgeDescription}
                    value={this.badge}
                  />
                )}
                {Tag === "button" && (
                  <qds-icon
                    aria-hidden="true"
                    class={{ "is-open": this.open }}
                    library="core"
                    name="dropdown"
                  />
                )}
              </div>
              <div
                class={{
                  line: true,
                  "pressed-line": Tag === "button" && this.open,
                }}
              />
            </Tag>
          )}

          <div
            class={{
              hide: !this.open,
            }}
          >
            <slot />
          </div>
        </div>
      </Host>
    );
  }

  private readonly onFocusHandler = (event: FocusEvent): void => {
    const target = event.currentTarget as HTMLElement;
    target.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
    this.onFocus(event);
  };

  private readonly onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event));
  };

  private readonly onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event));
  };
}
